import React from 'react'
import { useIntl } from 'react-intl'
import DataTable from '../DataTable'
import { getTheme } from '../../config'
import { formatDate } from '../../services/helper'
import { useLoanContext } from '../../contexts/LoanContext'
import { applyMask } from '../../services/utils'
import AddCoBorrowerButton from '../AddCoBorrowerButton'
import { addSpacesToPascalCase } from '../../services/helpers'

const theme = getTheme()

export type LoanUsersTableProps = {
	title?: string
}

type DataRowProps = {
	name: string
	email: string
	phone?: string | null
	role: string
	createdAt: string | null
}

const DEFAULT_SORT_COLUMN = 'createdAt'

const LoanUsersTable = ({ title }: LoanUsersTableProps) => {
	const { dataTable: breakpoint } = theme.breakpoints

	const { formatMessage } = useIntl()
	const {
		loan,
		isLoading: isLoanLoading,
		isRefetching: isLoanRefetching,
		refetch,
	} = useLoanContext()

	const loanUsers =
		loan?.userLoans.map((userLoan) => ({
			name: `${userLoan.user?.firstName} ${userLoan.user?.lastName}`,
			email: userLoan.user?.email || '',
			phone: userLoan.user?.phone,
			role: addSpacesToPascalCase(userLoan.role || ''),
			createdAt: formatDate(userLoan.user?.createdAt, false),
		})) || []

	const loanOfficer = loan?.loanOfficer?.email
		? [
				{
					name: loan.loanOfficer.name || '',
					email: loan.loanOfficer.email,
					phone: loan.loanOfficer.phone,
					role: 'LoanOfficer',
					createdAt: loan.loanOfficer.siteConfiguration?.createdAt,
				},
			]
		: []

	const data = [...loanUsers, ...loanOfficer]

	const columns = [
		{
			name: formatMessage({ id: 'global.name' }),
			selector: (row: DataRowProps) => row.name,
			cell: (row: DataRowProps) => row.name,
			sortable: true,
		},
		{
			name: formatMessage({ id: 'global.email' }),
			selector: (row: DataRowProps) => row.email || '',
			sortable: true,
		},
		{
			name: formatMessage({ id: 'global.phone_number' }),
			cell: (row: DataRowProps) => applyMask(row.phone, 'phone'),
		},
		{
			name: formatMessage({ id: 'global.role' }),
			selector: (row: DataRowProps) => {
				if (!row.role) {
					return ''
				}
				return addSpacesToPascalCase(row.role)
			},
		},
		{
			name: formatMessage({ id: 'global.created' }),
			selector: (row: DataRowProps) => row.createdAt || '',
			cell: (row: DataRowProps) => formatDate(row.createdAt, false),
			sortable: true,
			sortField: 'createdAt',
			hide: breakpoint,
		},
	]

	return (
		<div>
			<DataTable
				title={title}
				onRefreshClick={refetch}
				data={data}
				columns={columns}
				defaultSortAsc={false}
				defaultSortFieldId={DEFAULT_SORT_COLUMN}
				progressPending={isLoanLoading && !isLoanRefetching}
				keyField="id"
				titleActions={[<AddCoBorrowerButton key="add-cob-button" />]}
			/>
		</div>
	)
}

export default LoanUsersTable
